import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3, Undo, Redo } from 'lucide-react';

export default function RichTextEditor({ content, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none min-h-[300px] px-4 py-3',
        style: 'font-family: Nunito, sans-serif; color: #2C2A29; line-height: 1.8;'
      }
    }
  });

  if (!editor) return null;

  const ToolButton = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-orange-100' : 'hover:bg-gray-100'}`}
      style={{ color: isActive ? '#E57A44' : '#595553' }}
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#FFFFFF', borderColor: '#E6E2D8' }}>
      <div className="flex items-center gap-1 flex-wrap p-2 border-b" style={{ borderColor: '#E6E2D8', backgroundColor: '#FDFBF7' }}>
        <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2">
          <Heading2 className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3">
          <Heading3 className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <div className="w-px h-6 mx-1" style={{ backgroundColor: '#E6E2D8' }}></div>
        <ToolButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold (Ctrl+B)">
          <Bold className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic (Ctrl+I)">
          <Italic className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <div className="w-px h-6 mx-1" style={{ backgroundColor: '#E6E2D8' }}></div>
        <ToolButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet list">
          <List className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered list">
          <ListOrdered className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote">
          <Quote className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <div className="w-px h-6 mx-1" style={{ backgroundColor: '#E6E2D8' }}></div>
        <ToolButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo className="w-4 h-4" strokeWidth={2} />
        </ToolButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
